import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatCurrency, formatMonth, formatPercent } from '~/lib/format';
import { growthSeries, monthlySeries, type MonthGrowth } from '~/stores/dividend-store';
import { clearHover, hoverAt, setHoverAt } from '~/stores/crosshair-store';

// How much each month grew on the one before it.
//
// The companion to the chart on its left, and the thing that chart cannot show:
// a run of monthly totals climbing steadily and a run climbing and flattening
// are the same picture until you plot the rate.
//
// Bars from a zero rule, which is what a diverging measure wants. The sign is
// the whole finding here — growing or shrinking, not high or low — so zero is
// drawn, the scale always contains it, and each bar hangs off it in the
// direction of its answer. This is also why it is not zero-*based* like the
// payments chart beside it: there, zero is the floor; here it is the middle.
//
// Coloured, unlike every line in this app. A line carries its meaning in its
// path and colour would only decorate it; a bar carries its meaning in which
// side of the rule it is on, and that is a distinction two hues make instantly
// and a reader otherwise has to trace. Red and green are the app's money-lost
// and money-gained pair, which is exactly what a fall and a rise in what you
// were paid are.

const SEAM_UP_TO = 90;

/** The record, for a caller with a header to put it in. The latest reading is
    the one anybody quotes, so it leads. */
export const growthSummary = (): string | null => {
    const known = growthSeries().filter((g) => g.pct != null);
    if (known.length === 0) return null;
    const latest = known[known.length - 1];
    return `${formatMonth(latest.period)} · ${formatPercent(latest.pct, 1)}`;
};

export const GrowthChart: Component<{ height?: string }> = (props) => {
    const months = createMemo(() => growthSeries());
    const known = createMemo(() => months().filter((g) => g.pct != null));

    /** Months still to elapse before there are two readings to draw between.
        Zero once the wait is not the reason — enough months, but nothing paid in
        the ones being measured from. */
    const waiting = createMemo(() => Math.max(0, 3 - monthlySeries().length));

    const span = createMemo(() => {
        const all = known();
        if (all.length === 0) return null;
        const values = all.map((g) => g.pct!);
        // Always contains zero: the sign is the subject, and a window that
        // excludes it would draw a series shrinking by 4% as though it were
        // rising.
        const lo = Math.min(0, ...values);
        const hi = Math.max(0, ...values);
        const range = hi - lo || 1;
        // Where the rule sits, as a percentage from the top.
        return { lo, hi, range, zero: (hi / range) * 100 };
    });

    const [over, setOver] = createSignal(false);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });

    /** The shared date, snapped to a month — same arrangement as the payments
        chart, so hovering one marks the same month on both. */
    const hover = (): number | null => {
        const t = hoverAt();
        const all = months();
        if (t == null || all.length === 0) return null;
        let found: number | null = null;
        for (let i = 0; i < all.length; i += 1) {
            if (Date.parse(`${all[i].period}-01T00:00:00`) > t) break;
            found = i;
        }
        return found;
    };
    const at = (): MonthGrowth | null => (hover() == null ? null : (months()[hover()!] ?? null));

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const count = months().length;
        if (rect.width <= 0 || count === 0) return;
        const fraction = Math.min(0.999, Math.max(0, (e.clientX - rect.left) / rect.width));
        const month = months()[Math.floor(fraction * count)];
        if (month) setHoverAt(Date.parse(`${month.period}-01T00:00:00`));
        setCursor({ x: e.clientX, y: e.clientY });
        setOver(true);
    };

    /** One bar, positioned off the rule rather than off an edge: a rise grows up
        from zero and a fall grows down from it, and both start in the same
        place. */
    const bar = (pct: number): { top: string; height: string } => {
        const s = span()!;
        const size = (Math.abs(pct) / s.range) * 100;
        return pct >= 0 ? { top: `${(s.zero - size).toFixed(2)}%`, height: `${size.toFixed(2)}%` } : { top: `${s.zero.toFixed(2)}%`, height: `${size.toFixed(2)}%` };
    };

    return (
        <Show
            when={span()}
            fallback={
                <div class='flex min-h-0 flex-1 items-center justify-center px-4 text-center'>
                    {/* Says what is missing rather than that there is nothing. A
                        rate needs a month to measure from, and "no data" on a
                        book that is plainly paying reads as a bug. */}
                    <Text tone='muted'>{waiting() === 0 ? 'Nothing paid in the months before these' : `${waiting()} more ${waiting() === 1 ? 'month' : 'months'} of payments to compare`}</Text>
                </div>
            }>
            {/* Padded to match the chart beside it — see the note there. The
                plot is the inner box, so the zero rule and the bars share one
                containing block and cannot drift apart by the padding. */}
            <div class={`w-full px-3 py-3 ${props.height ?? 'h-44'}`}>
                <div
                    class={`relative flex h-full w-full ${months().length <= SEAM_UP_TO ? 'gap-px' : ''}`}
                    onMouseMove={track}
                    onMouseLeave={() => {
                        setOver(false);
                        clearHover();
                    }}>
                {/* The rule, under the bars and across the whole width. Not a
                    reference the eye may take or leave — it is the line the
                    chart is about being above or below. */}
                    <div class='pointer-events-none absolute right-0 left-0 h-px bg-muted-foreground/70' style={{ top: `${span()!.zero.toFixed(2)}%` }} />

                    <For each={months()}>
                        {(g, i) => (
                            <div class={`relative h-full min-w-0 flex-1 ${hover() === i() ? 'bg-muted' : ''}`}>
                                {/* A month whose base paid nothing has no
                                    percentage — growth from zero is not a large
                                    number, it is not a number — so it draws no
                                    bar. The column is still there to hover, and
                                    the tooltip says why. */}
                                <Show when={g.pct != null}>
                                    <div class={`absolute right-0 left-0 ${g.pct! >= 0 ? 'bg-green' : 'bg-red'}`} style={bar(g.pct!)} />
                                </Show>
                            </div>
                        )}
                    </For>
                </div>
            </div>

            {/* Carries both months, because a percentage on its own is the one
                number here you cannot sanity-check: +140% is a different story on
                $40 than on $400, and the two amounts say which it was. */}
            <Show when={over() && at()}>
                <Portal>
                    <div
                        class='pointer-events-none fixed z-[200] rounded border bg-background px-2 py-1 text-foreground shadow'
                        style={{ left: `${Math.min(cursor().x + 12, window.innerWidth - 240)}px`, top: `${Math.min(cursor().y + 12, window.innerHeight - 72)}px` }}>
                        <div class='flex items-baseline gap-2 whitespace-nowrap'>
                            <Text tone='muted'>{formatMonth(at()!.period)}</Text>
                            <Show when={at()!.pct != null} fallback={<Text tone='muted'>no rate</Text>}>
                                <Text tone={at()!.pct! >= 0 ? 'green' : 'red'} weight='medium'>
                                    {formatPercent(at()!.pct, 1)}
                                </Text>
                            </Show>
                        </div>
                        <Text tone='muted' class='block whitespace-nowrap'>
                            {formatCurrency(at()!.then)} → {formatCurrency(at()!.now)}
                        </Text>
                    </div>
                </Portal>
            </Show>
        </Show>
    );
};

export default GrowthChart;
